import { Card } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Card';
import { Stars } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/StarRating/Stars';
import { Clock } from '@johnlewispartnership/wtr-ingredients/dist/foundations/icons';
import { Typography } from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';
import { Button } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Button';

import type { RecipeSummaryCard } from 'services/recipes/types';

import locator from 'locator';
import { recipeURL, selectImage } from '../utils/formatRecipe';
import styles from './RecipeList.module.scss';

type RecipeListProps = {
  recipes: RecipeSummaryCard[];
  hasNoFilteredResults?: boolean;
};

const formatRecipeDuration = (minutesTotal: number) => {
  const hours = Math.floor(minutesTotal / 60);
  const minutes = minutesTotal % 60;

  const formattedHours = hours ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
  const formattedMinutes = minutes ? `${minutes} mins` : '';

  return `${formattedHours}${formattedHours && formattedMinutes ? ' ' : ''}${formattedMinutes}`;
};

const NoFilteredResults = () => {
  const handleClearFilters = () => {
    window.history.pushState(null, '', locator.pathname);
  };

  return (
    <div className={styles.noFilteredResults}>
      <Typography styleAs="paragraph">
        Sorry, we currently have no recipes matching your search
      </Typography>
      <Button theme="secondary" onClick={handleClearFilters}>
        Search again
      </Button>
    </div>
  );
};

const RecipeList = ({ recipes, hasNoFilteredResults }: RecipeListProps) => {
  if (hasNoFilteredResults) {
    return <NoFilteredResults />;
  }

  return (
    <ul className={styles.grid} data-testid="recipe-card-list">
      {recipes.map((recipe, index) => (
        <li key={recipe.id}>
          <Card
            interactive
            href={recipeURL(recipe)}
            fullWidth
            fullHeight
            className={styles.card}
            image={
              <img alt="" src={selectImage(recipe)} loading={index > 2 ? 'lazy' : undefined} />
            }
            footer={
              recipe.totalTime ? (
                <Typography
                  noMargins
                  styleAs="paragraphSmallLight"
                  className={styles.duration}
                  data-testid="recipe-duration"
                >
                  <Clock size="xsmall" />
                  {formatRecipeDuration(recipe.totalTime)}
                  <span className={styles.srOnly}>cooking time</span>
                </Typography>
              ) : (
                ''
              )
            }
          >
            <div className={styles.ratingTitle}>
              <Typography
                noMargins
                styleAs="sectionHeading"
                component="h3"
                className={styles.title}
              >
                {recipe.title}
              </Typography>

              {recipe.totalRatings > 0 && (
                <div className={styles.ratingSummary} data-testid="rating-summary">
                  <Stars
                    starSize="xsmall"
                    rating={recipe.averageRating!}
                    aria-label={`${recipe.averageRating} out of 5`}
                  />
                  <Typography
                    noMargins
                    element="span"
                    styleAs="paragraphHeading"
                    aria-label={`${recipe.totalRatings} ratings`}
                  >
                    ({recipe.totalRatings})
                  </Typography>
                </div>
              )}
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
};

export default RecipeList;
